import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Instagram } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ShareFeature } from "@/components/site/ShareFeature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NETWORKS = ["VLMAG", "VLMAG MUSIC", "VLMAG STYLE"];

const empty = { name: "", email: "", network: "VLMAG", category: "talent", discipline: "", links: "", message: "" };

const REASONS = [
  { id: "talent", label: "Music / Fashion / Style" },
  { id: "advertising", label: "Advertising / Collab" },
  { id: "join", label: "Join the Team" },
];

const FOLLOWS = [
  { handle: "@vlmagonline", id: "vlmagonline", href: "https://www.instagram.com/vlmagonline" },
  { handle: "@vlmagmusic", id: "vlmagmusic", href: "https://www.instagram.com/vlmagmusic" },
  { handle: "@vlmagstyle", id: "vlmagstyle", href: "https://www.instagram.com/vlmagstyle" },
];

export const GetFeatured = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, form);
      setDone(true);
      setForm(empty);
      toast.success("Submission received. The VLMAG team will be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "rounded-none bg-transparent border-white/20 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600 h-12";

  return (
    <section id="featured" data-testid="get-featured" className="px-5 md:px-10 py-20 md:py-32 border-t border-white/10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">Join The Network</p>
          <h2 className="font-display uppercase leading-[0.88] tracking-tight text-5xl md:text-7xl">
            Get<br />Featured
          </h2>
          <p className="font-serif-ed italic text-2xl md:text-3xl text-white mt-6">Your story is the culture.</p>
          <p className="text-neutral-400 leading-relaxed mt-6 text-base md:text-lg max-w-md">
            Artists, designers, DJs, brands and creatives — tell us what you're building. We document,
            support and elevate the people shaping what comes next.
          </p>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="submission-success"
              className="border border-[#E30613] p-10 md:p-14"
            >
              <p className="font-display uppercase text-3xl md:text-4xl text-white">Submission Received.</p>
              <p className="text-neutral-400 mt-4 leading-relaxed">
                Thank you for reaching out. The VLMAG editorial team reviews every submission — we'll be in touch soon.
              </p>

              <div className="mt-8">
                <ShareFeature
                  shareUrl={typeof window !== "undefined" ? window.location.origin : "https://vlmagnetwork.com"}
                  title="VLMAG — Fashion, Music, Art & Culture"
                  variant="full"
                  hideCard
                  testidPrefix="featured-share"
                  eyebrow="While you wait — put VLMAG on"
                  heading="Spread The Network"
                  subtext="Share VLMAG with your community. One tap sends people straight to the network documenting the culture — the more eyes, the bigger the features."
                />
              </div>

              <div className="mt-6 border border-white/10 p-6 md:p-7" data-testid="featured-follow">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-4">Follow the networks — never miss a drop</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {FOLLOWS.map((f) => (
                    <a
                      key={f.id}
                      href={f.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`follow-${f.id}`}
                      className="group inline-flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border border-white/20 text-white hover:border-[#E30613] hover:text-[#E30613] transition-colors"
                    >
                      <Instagram size={15} /> {f.handle}
                    </a>
                  ))}
                </div>
              </div>

              <button
                data-testid="submit-another"
                onClick={() => setDone(false)}
                className="mt-8 relative overflow-hidden border border-white/30 px-7 py-3 text-xs uppercase tracking-[0.2em] font-bold group"
              >
                <span className="relative z-10">Submit Another</span>
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="submission-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Name *</Label>
                  <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Email *</Label>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={`mt-2 ${inputCls}`} />
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">I'm reaching out about *</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="reason-select">
                  {REASONS.map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      data-testid={`reason-option-${r.id}`}
                      onClick={() => setForm((f) => ({ ...f, category: r.id }))}
                      className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors duration-300 ${
                        form.category === r.id ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-400 hover:border-white/60"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Which Network</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="network-select">
                  {NETWORKS.map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-testid={`network-option-${n.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, network: n }))}
                      className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors duration-300 ${
                        form.network === n ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/20 text-neutral-400 hover:border-white/60"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Discipline</Label>
                  <Input data-testid="input-discipline" value={form.discipline} onChange={set("discipline")} placeholder="e.g. DJ, Designer, Founder" className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Links / Portfolio</Label>
                  <Input data-testid="input-links" value={form.links} onChange={set("links")} placeholder="Instagram, website, etc." className={`mt-2 ${inputCls}`} />
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Your Story *</Label>
                <Textarea data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us what you're building and why it matters." rows={4} className={`mt-2 rounded-none bg-transparent border-white/20 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600`} />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-button"
                className="relative overflow-hidden bg-[#E30613] px-10 py-4 text-xs uppercase tracking-[0.24em] font-bold text-white w-full sm:w-auto disabled:opacity-60 group"
              >
                <span className="relative z-10">{submitting ? "Sending..." : "Submit Your Work"}</span>
                <span className="absolute inset-0 bg-white/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-400" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
